<script setup lang="ts">
    import { TRAINING_STATUS_LABEL_KEYS, type TrainingsListingItem } from '~/types/academy/baseTraining';
    import { enrichTeaserBadges, removeDuplicateBadges } from '~/utils/academyUtils';
    import { isExternalUrl } from '~/utils/prefixLocalePath';

    const { item, horizontal = false } = defineProps<{
        item: TrainingsListingItem;
        horizontal?: boolean;
    }>();

    const STATUS_BADGE_TYPES = Object.keys(TRAINING_STATUS_LABEL_KEYS);

    const allBadges = computed(() => removeDuplicateBadges(enrichTeaserBadges(item)));

    const badgesWithoutStatus = computed(() =>
        allBadges.value.filter((badge) => !STATUS_BADGE_TYPES.includes(badge.type))
    );

    const displayItem = computed(() => ({
        ...item,
        badges: allBadges.value
    }));

    const openCtaInNewTab = computed(() => isExternalUrl(item.ctaUrl ?? ''));

    const isPlayerOpen = ref(false);

    const refreshAcademyCourseData = useAcademyCourseRefresh();

    const courseId = computed(() => (item.type === 'course' ? item.courseId : undefined));

    /** Only marks the cached course stale: the card is brought up to date when the player closes. */
    function onCourseStatusChanged() {
        if (courseId.value) {
            invalidateAcademyCourseCache(courseId.value);
        }
    }

    function onPlayerClose() {
        isPlayerOpen.value = false;

        if (courseId.value) {
            refreshAcademyCourseData(courseId.value);
        }
    }
</script>

<template>
    <div
        :class="{
            'teaser-card': true,
            'teaser-card--horizontal': horizontal,
            'teaser-card--orange': displayItem.color === 'orange',
            'teaser-card--pink': displayItem.color === 'pink',
            'teaser-card--blue': displayItem.color === 'blue' || !displayItem.color
        }"
    >
        <TeaserImage
            :item="displayItem"
            wide
            :href="displayItem.ctaUrl"
            :open-new-tab="openCtaInNewTab"
            :completed="displayItem.status === 'completed'"
        >
            <template #badges>
                <div class="badges-vertical">
                    <TeaserBadges
                        :badges="displayItem.badges"
                        class="vertical-only"
                    />
                </div>
            </template>
        </TeaserImage>
        <div class="teaser-content">
            <div class="content-wrapper">
                <div class="title-vertical">
                    <h3 class="teaser-title">{{ displayItem.title }}</h3>
                </div>

                <div class="badges-horizontal">
                    <TeaserBadges
                        :badges="badgesWithoutStatus"
                        class="horizontal-only"
                    />
                </div>
                <div class="title-horizontal">
                    <h3 class="teaser-title">{{ displayItem.title }}</h3>
                </div>
                <AcademyTrainingProgress
                    v-if="displayItem.type === 'course'"
                    :status="displayItem.status"
                    class="training-progress-horizontal"
                />
                <TeaserCardDetails :item="displayItem" />
            </div>
            <div class="teaser-actions">
                <div class="teaser-cta">
                    <PrimaryButton
                        :button-href="displayItem.ctaUrl"
                        :button-text="$t('academy.buttonLabel.learnMore')"
                        :has-icon="true"
                        :size="'small'"
                        :alignment="'center'"
                        :stretch="true"
                        :open-new-tab="openCtaInNewTab"
                    >
                    </PrimaryButton>
                </div>
                <template v-if="displayItem.type === 'course' && displayItem.courseId">
                    <button
                        type="button"
                        class="teaser-play"
                        :aria-label="$t('academy.training.cta.startAriaLabel')"
                        @click="isPlayerOpen = true"
                    >
                        <Icon
                            class="teaser-play-icon"
                            name="hfp:circle-play"
                            mode="svg"
                            size="18px"
                        />
                    </button>
                    <BasicModal
                        :is-open="isPlayerOpen"
                        :close-on-click-outside="false"
                        full-screen
                        :aria-label="$t('academy.training.player.ariaLabel')"
                        @close="onPlayerClose"
                    >
                        <template #content>
                            <div class="player-modal-content">
                                <DoceboCoursePlayer
                                    v-if="isPlayerOpen"
                                    :id="`academy-training-player-${displayItem.courseId}`"
                                    :course-id="displayItem.courseId"
                                    @course-status-changed="onCourseStatusChanged"
                                />
                            </div>
                        </template>
                    </BasicModal>
                </template>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/academy/teaser-layout.scss';

    .teaser-card {
        max-width: 25rem;
        display: flex;
        flex-direction: column;
        width: 100%;
        min-height: 427px; // Figma card height
        border: 0;
        border-radius: $border-radius-s;
        overflow: hidden;

        &--pink {
            --progress-bg: var(--brand-tertiary-10);
            background-color: var(--brand-tertiary-10);
        }

        &--orange {
            --progress-bg: var(--brand-secondary-30);
            background-color: var(--brand-secondary-30);
        }

        &--blue {
            --progress-bg: var(--brand-primary-10);
            background-color: var(--brand-primary-10);
        }

        @media (max-width: $br-xs) {
            max-width: 100%;
        }

        .teaser-content {
            display: flex;
            flex-direction: column;
            gap: $spacing--05;
            padding: var(--spacing);
            flex: 1;
            justify-content: start;
        }

        .content-wrapper {
            display: flex;
            flex-direction: column;
            gap: $spacing;
            flex: 1;
            justify-content: start;
        }

        .teaser-title {
            max-width: 100%;
            color: $black;
            font-size: $font-m;
            font-weight: 700;
            line-height: 1.18;
            letter-spacing: -0.01em;
            word-break: break-word;
        }

        .teaser-actions {
            margin-top: $spacing;
            display: flex;
            flex-direction: row;
            gap: $spacing--05;

            .teaser-cta {
                flex: 1;
            }

            .teaser-play {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                aspect-ratio: 1/1;
                height: 100%;
                padding: 0;
                border: 0;
                border-radius: var(--border-radius);
                background: $brand-primary;
                color: #ffffff;
                cursor: pointer;

                :deep(.teaser-play-icon),
                :deep(.teaser-play-icon *) {
                    stroke-width: 2;
                }
            }
        }

        .teaser-image {
            width: 100%;
            margin-bottom: $spacing--025;
        }

        .badges-horizontal {
            display: none;
        }

        .training-progress-horizontal {
            display: none;
        }

        .title-horizontal {
            display: none;
            @include teaser-horizontal-layout {
                display: inherit;
            }
        }

        .title-vertical {
            display: inherit;
            @include teaser-horizontal-layout {
                display: none;
            }
        }
    }

    /* not nested in .teaser-card: BasicModal teleports its content to the body */
    .player-modal-content {
        height: 100%;
        min-height: 0;

        :deep(dcbo-course-player) {
            display: block;
            height: 100%;
            width: 100%;
        }
    }

    .teaser-card--horizontal {
        flex-direction: column;
        max-width: inherit;

        .teaser-title {
            max-width: 100%;
        }

        .teaser-actions {
            display: flex;
            margin-top: $spacing;

            .teaser-cta {
                width: 100%;
            }

            .teaser-play {
                width: 2.5rem;
                height: 2.5rem;
            }
        }

        .badges-vertical {
            display: inherit;
        }

        @include teaser-horizontal-layout {
            flex-direction: row;
            min-height: auto;

            .teaser-title {
                max-width: 100%;
            }

            .teaser-image {
                width: 17.5rem; // 280px
                min-width: 0;
                flex-shrink: 1;
                margin-bottom: 0;
                padding-right: 0;
            }

            .teaser-actions {
                min-width: 17.125rem; // 274px
                align-self: end;
                margin-top: 0;

                .teaser-play {
                    width: auto;
                    height: 100%;
                }
            }

            .badges-horizontal {
                display: inherit;
            }

            .badges-vertical {
                display: none;
            }

            .training-progress-horizontal {
                display: flex;
            }

            :deep(.teaser-card-details) {
                flex-direction: row;
                flex-wrap: wrap;
                column-gap: var(--spacing--2);
            }
        }
    }
</style>
